<template>
  <div class="px-4 border rounded-10 border-slate-200 dark:border-zinc-700 bg-slate-100 dark:bg-zinc-800" data-testid="viewer-versions-current">
    <div class="py-4 flex items-center gap-2.5">
      <div class="flex items-center justify-center w-40 h-40 border rounded-md border-slate-200 bg-white">
        <img :src="posterUrl" class="object-contain w-full h-full" />
      </div>
      <div class="flex flex-col gap-1 min-w-40">
        <p class="px-3 font-bold text-sm">Current version</p>
        <p class="px-3 text-sm">{{ formatDate(updatedAt) }}</p>
        <p class="pt-2">
          <Button
            type="link"
            variant="black"
            icon="general.openInNewTab"
            icon-size="xs"
            data-testid="viewer-versions-current-button-preview"
            @click.prevent="openInNewTab(posterUrl)"
            >Preview</Button
          >
        </p>
        <p>
          <Button
            type="link"
            variant="black"
            icon="general.copyDocument"
            icon-size="xs"
            data-testid="viewer-versions-current-button-copy"
            :loading="isCopying"
            :disabled="isCopying"
            @click.prevent="onDuplicateClick"
            >Duplicate</Button
          >
        </p>
      </div>
    </div>

    <HorizontalDivider />
    <div class="py-4 font-semibold text-sm">
      <p v-if="hasVersionsToRestore">Only you can see your edits history. We keep it private.</p>
      <p v-else-if="versioningEnabled">If you annotate or edit your content, historical versions will appear here.</p>
      <div v-else class="flex gap-2 items-center">
        <Icon name="toast.info" />
        <p class="font-semibold text-sm">
          Make sure your custom storage is
          <a
            href="https://support.zight.com/hc/en-us/articles/6577742705303-How-can-I-use-my-own-Amazon-AWS-S3-bucket-storage-with-Zight"
            target="_blank"
            class="text-blue-500 hover:text-blue-600 hover:underline"
            >correctly set up</a
          >
          to access Versions.
        </p>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { Icon, Button, HorizontalDivider } from '@/storybook';
import { useVersionsStore } from '@/modules/viewer/store/useVersionsStore';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useViewerTrack, TrackEvents } from '@/modules/viewer/composables/useViewerTrack';

const viewerItemStore = useViewerItemStore();
const versionsStore = useVersionsStore();

const { hasVersionsToRestore } = storeToRefs(versionsStore);
const { posterUrl, updatedAt, isCopying } = storeToRefs(viewerItemStore);
const itemStore = useViewerItemStore();
const { trackItemOwner } = useViewerTrack();
const { versioningEnabled } = storeToRefs(useOrganizationStore());

const openInNewTab = (url?: string) => {
  if (!url) {
    return;
  }
  window.open(url, '_blank');
};

const onDuplicateClick = () => {
  trackItemOwner(TrackEvents.VERSIONS_MODAL_DUPLICATE_CLICK);
  itemStore.copyCurrentItem({ duplicate: true });
};

const formatDate = (date?: string) => (date ? new Date(date).toLocaleString() : '');
</script>
